import {createEntityAdapter, createSlice} from "@reduxjs/toolkit"
import {StoreState} from "store"
import {useSelector} from "react-redux"
import {ApplicationOrderResponse, ReportResponse} from "types/Report"
import {fetchReportByApplicationId} from "./fetchReportByApplicationId"
import {fetchApplicationOrder} from "./fetchApplcationOrder"
import {approveApplication} from "../applications/approveApplication"
import {rejectApplication} from "../applications/rejectApplication"

interface ReportEntity extends ReportResponse {
    application_id: number
}

export const reportAdapter = createEntityAdapter<ReportEntity>({
    selectId: report => report?.id
})

export interface StateProps {
    loading: boolean
    order: {
        loading: boolean
        application_id?: number
        details?: ApplicationOrderResponse
    }
}

const initialState = reportAdapter.getInitialState<StateProps>({
    loading: true,
    order: {
        loading: false,
        application_id: undefined,
        details: undefined
    }
})

const reportSlice = createSlice({
    name: "report",
    initialState,
    reducers: {},
    extraReducers: builder => {
        // Вывод всех заявок
        builder.addCase(fetchReportByApplicationId.pending, state => {
            state.loading = true
        })
        builder.addCase(fetchReportByApplicationId.fulfilled, (state, action) => {
            if (action.payload?.data?.report) {
                reportAdapter.setOne(state, {
                    ...action.payload.data,
                    application_id: action.meta.arg
                })
            }
            state.loading = false
        })
        builder.addCase(fetchReportByApplicationId.rejected, state => {
            state.loading = false
        })

        // Вывод заказа по заявке
        builder.addCase(fetchApplicationOrder.pending, (state, action) => {
            state.order.loading = true
            state.order.application_id = action.meta.arg
            state.order.details = undefined
        })
        builder.addCase(fetchApplicationOrder.fulfilled, (state, action) => {
            state.order.loading = false
            state.order.application_id = action.meta.arg
            state.order.details = action.payload?.data
        })
        builder.addCase(fetchApplicationOrder.rejected, state => {
            state.order.loading = false
            state.order.details = undefined
        })

        builder.addCase(approveApplication.pending, state => {
            state.loading = true
        })
        builder.addCase(approveApplication.fulfilled, state => {
            state.loading = false
        })
        builder.addCase(approveApplication.rejected, state => {
            state.loading = false
        })
        builder.addCase(rejectApplication.pending, state => {
            state.loading = true
        })
        builder.addCase(rejectApplication.fulfilled, state => {
            state.loading = false
        })
        builder.addCase(rejectApplication.rejected, state => {
            state.loading = false
        })
    }
})

export const {selectById, selectAll} = reportAdapter.getSelectors<StoreState>(state => state.report)

// Загрузка отчета
export const useLoadingReport = () => useSelector((state: StoreState) => state.report.loading)
// Вывод отчета
export const useSelectReportByApplicationId = (applicationId: number) =>
    useSelector((state: StoreState) => selectAll(state).find(r => r.application_id === applicationId))
// Вывод заказа заявки (только если он относится к текущей заявке)
export const useSelectOrderByApplicationId = (applicationId: number) =>
    useSelector((state: StoreState) =>
        state.report.order.application_id === applicationId ? state.report.order : undefined
    )

export default reportSlice.reducer
